import { apiClient } from '@/app/utils/Request';

export interface ApiResponse<T = unknown> {
  code: number;
  message: string;
  data: T;
}

export type CartStatus = 'active' | 'removed' | string;

// 后端 /cart 查询返回是 cart + service_forms + processes 的 LEFT JOIN 扁平字段
export interface CartListRow {
  // cart 表字段
  id: number;
  user_id: number;
  product_name: string | null;
  part_use_type: string | null;
  shipping_address_id: number | null;
  form_id: number;
  display_code: string | null;
  display_name: string | null;
  unit_price: number | null;
  total_price: number | null;
  currency: string | null;
  status: CartStatus;
  attachments: string;
  // service_forms.quantity（如果后端查询包含该字段）
  quantity?: number | null;
  // 后端在 /cart/active 返回附件详情（用于前端展示与下载）
  attachments_files?: Array<{
    id?: number;
    file_name: string;
    file_address: string;
  }>;
  created_at: string;
  updated_at: string;

  // service_forms 字段（前缀 sf_）
  sf_id: number | null;
  sf_product_name?: string | null;
  sf_user_id: number | null;
  sf_process_id: number | null;
  sf_source_page: string | null;
  sf_form_version: string | null;
  sf_process_value: string | null;
  sf_material_id: number | string | null;
  sf_post_process_ids: string | null;
  sf_tolerance_id: number | string | null;
  sf_surface_req_id: number | string | null;
  sf_surface_rough_id: number | string | null;
  sf_surface_treatment_id?: number | string | null;
  sf_mold_texture_id?: number | string | null;
  sf_other_params_json: unknown;
  /** customer_remarks */
  sf_remarks: string | null;
  /** internal remark for admin/backend usage */
  sf_internal_remarks?: string | null;
  sf_status: string | null;
  sf_attachments_id: string | null;
  sf_quantity?: number | null;
  sf_created_at: string | null;
  sf_updated_at: string | null;

  // processes 字段（前缀 process_）
  process_id: number | null;
  process_code: string | null;
  process_name: string | null;
  process_description: string | null;
  process_is_active: 0 | 1 | null;
  process_sort_order: number | null;
}

export interface CreateCartItemPayload {
  form_id: number;
  attachments: string; // 必填（后端校验）

  product_name?: string | null;
  part_use_type?: string | null;
  shipping_address_id?: number | null;
  display_code?: string | null;
  display_name?: string | null;
  unit_price?: number | null;
  total_price?: number | null;
  currency?: string | null;
  status?: CartStatus; // 默认 active
}

export interface CopyCartPayload {
  cart_ids: number[] | string; // 支持数组或 "1,2,3"
  status?: CartStatus; // 默认 active
}

export interface CopyCartResult {
  formMap: Record<number, number>;
  newCartIds: number[];
}

export type UpdateCartItemPayload = Partial<CreateCartItemPayload>;

// 获取指定用户的购物车列表（默认 active）
// GET /cart/user/:userId?status=active
export function getCartByUser(userId: number | string, status: string = 'active') {
  return apiClient.get<ApiResponse<CartListRow[]>>(`/cart/user/${userId}`, { status });
}

// 获取当前用户 active 购物车列表
// GET /cart/active
export function getActiveCart() {
  return apiClient.get<ApiResponse<CartListRow[]>>('/cart/active');
}

// 新建购物车行
// POST /cart
export function createCartItem(data: CreateCartItemPayload) {
  return apiClient.post<ApiResponse<CartListRow>>('/cart', data);
}

// 修改购物车行
// PUT /cart/:id
export function updateCartItem(id: number | string, data: UpdateCartItemPayload) {
  return apiClient.put<ApiResponse<CartListRow>>(`/cart/${id}`, data);
}

// 删除购物车行（软删除：status=removed）
// DELETE /cart/:id
export function deleteCartItem(id: number | string) {
  return apiClient.delete<ApiResponse<null>>(`/cart/${id}`);
}

// 复制购物车
// POST /cart/copy
export function copyCartItems(data: CopyCartPayload) {
  return apiClient.post<ApiResponse<CopyCartResult>>('/cart/copy', data);
}
